import auth from '@react-native-firebase/auth';
import { KnownMediums, RemoteConfigVariables } from 'fitbeat.models';
import React, { PureComponent } from 'react';
import { translate } from 'react-i18next';
import { BackHandler, Dimensions, Keyboard, Platform, ScrollView, StyleSheet, Text, View } from 'react-native';
import { connect } from 'react-redux';
import { Field, reduxForm, SubmissionError } from 'redux-form';
import { Button, ButtonSize } from '../components';
import { GoogleAnalytics } from '../services/google-analytics';
import Theme from '../theme';
import { FormError, TextInput } from '../ui';
import { RemoteConfigWrapper } from '../ui/components/RemoteConfigWrapper';
import { isNameValid } from '../utils';
import { Logger } from '../utils';
import { UserDataConsumer } from '../utils/';
import { focusOnInput } from '../utils/FocusOnInput';
import { clearDeepLinkParams } from '../utils/notifications/actions';
import { defaultScale, scaleText } from '../utils/scaling';
import { TermsAndConditions } from './components';
import { addLegalAgreements, getAuthProvider } from './components/utils';
import { styles as SharedStyles } from './SignupFormsStyles';
import { textInputDarkStyle } from './SignupFormsStyles';

const { width } = Dimensions.get('window');
const isMobileWeb = width <= 550 && Platform.OS === 'web';
const isWeb = Platform.OS === 'web';

interface IProps {
  error?: Error;
  navigation: any;
  workoutInviteId?: string;
  initialize: any;
  user: any;
  utm_medium?: Medium;
  planId?: string;
  clearDeepLinkParams(params: string[]): void;
  t(key: string, options?: any): string;
  handleSubmit(cd: any): void;
}

interface IState {
  termsAccepted: boolean;
  isLoading: boolean;
  isVideoVisible: boolean;
}

class CompleteHomeProfileScreen extends PureComponent<IProps, IState> {
  public state = {
    termsAccepted: false,
    isLoading: false,
    isVideoVisible: false,
    userProfile: null,
  };

  private googleAnalytics = new GoogleAnalytics();
  private lastnameInput: any;
  private emailInput: any;
  private readonly didFocusSubscription;
  private willBlurSubscription;

  constructor(props: any) {
    super(props);
    this.didFocusSubscription = props.navigation.addListener('didFocus', () =>
      BackHandler.addEventListener('hardwareBackPress', this.onBackButtonPressAndroid),
    );
  }

  public componentDidMount = async () => {
    const { initialize, user: {
      userProfile,
    } } = this.props;
    initialize(userProfile);
    this.willBlurSubscription = this.props.navigation.addListener('willBlur', () =>
      BackHandler.removeEventListener('hardwareBackPress', this.onBackButtonPressAndroid),
    );
  }

  public onBackButtonPressAndroid = () => {
    BackHandler.exitApp();
    return true;
  }

  public componentWillUnmount() {
    if (this.didFocusSubscription) {
      this.didFocusSubscription.remove();
    }
    if (this.willBlurSubscription) {
      this.willBlurSubscription.remove();
    }
    BackHandler.removeEventListener('hardwareBackPress', this.onBackButtonPressAndroid);
  }

  public submit = async (values: any) => {
    const { t, user: {
      userProfile,
    } } = this.props;
    const { termsAccepted } = this.state;

    if (!termsAccepted) {
      throw new SubmissionError({ _error: new Error(t('pleaseAcceptTerms')) });
    }

    this.setState({ isLoading: true });
    try {
      addLegalAgreements(userProfile);
      await this.props.user.userProfileService.updateUserProfile({
        ...userProfile,
        firstname: values.firstname,
        lastname: values.lastname,
      });
      const provider = getAuthProvider(auth().currentUser);
      this.googleAnalytics.logEventWithParamaters('sign_up', { method: provider });
      this.props.navigation.navigate('HomeOnboardingScreens');
    } catch (e) {
      Logger.notify(e);
      throw new SubmissionError({ _error: new Error(t('failedToCompleteProfile')) });
    } finally {
      this.setState({ isLoading: false });
      this.props.clearDeepLinkParams(['utm_medium', 'utm_source', 'utm_campaign']);
    }
  }

  public acceptTermsAndConditions = () => {
    this.setState({ termsAccepted: !this.state.termsAccepted });
    Keyboard.dismiss();
  }

  public heading = () => {
    const { t } = this.props;
    return (
      <Text style={styles.heading}>
        <Text style={[SharedStyles.headerTextBold]}>{t('createAProfileHeadingBold')}
        </Text> {t('createAProfileHeadingNormal')}
      </Text>
    );
  }

  public render() {
    const { t, error, handleSubmit, utm_medium, planId } = this.props;
    const { isLoading, termsAccepted } = this.state;

    const isInfluencerSignup = utm_medium === KnownMediums.influencer;

    return (
      <>
        <ScrollView
          keyboardShouldPersistTaps='handled'
          style={[SharedStyles.container, styles.container]}
          contentContainerStyle={styles.contentContainerStyle}>
          <View>
            <View style={styles.headingContainer}>
              {this.heading()}
            </View>
            <View style={styles.formContainer}>
              <Field
                name='firstname'
                returnKeyType='next'
                autoCapitalize='words'
                placeholder={t('formFields:firstname')}
                height={45}
                component={TextInput}
                validate={isNameValid}
                keyboardType='default'
                onSubmitEditing={() => focusOnInput(this.lastnameInput)}
                {...isWeb && { ...textInputDarkStyle }}
              />
              <Field
                forwardRef
                name='lastname'
                returnKeyType='next'
                autoCapitalize='words'
                placeholder={t('formFields:lastname')}
                component={TextInput}
                validate={isNameValid}
                height={45}
                keyboardType='default'
                ref={(ref) => {
                  this.lastnameInput = ref;
                }}
                onSubmitEditing={() => focusOnInput(this.emailInput)}
                {...isWeb && { ...textInputDarkStyle }}
              />
            </View>

            <TermsAndConditions
              canAccept={true}
              isMinor={false}
              textStyle={styles.tandcText}
              preAgreementText={t('byCreatingYouAgreeToTandC')}
              acceptTermsAndConditions={this.acceptTermsAndConditions}
              termsAccepted={termsAccepted}
            />

            <FormError color={Theme.WHITE} error={error}/>

            <RemoteConfigWrapper remoteConfigKey={RemoteConfigVariables.free_trial_days}
                                 childPropConfiguredUsingRemoteConfig={
                                   (freeTrialDays) => ({
                                     title: planId
                                       ? t('createHomeAccountScreenMainActionForPaidUsers')
                                       : t('createHomeAccountScreenMainActionForTrialUsers', { freeTrialDays }),
                                   })}>
              <Button
                disabled={!this.state.termsAccepted}
                size={ButtonSize.MEDIUM}
                isLoading={isLoading}
                fontColor={Theme.WHITE}
                overrideFontStyles={{ fontFamily: Theme.Custom800, fontSize: 19 }}
                containerStyle={{ marginBottom: 10, marginTop: 15, height: 60 }}
                onPress={handleSubmit(this.submit)}
              />
            </RemoteConfigWrapper>
            {!isWeb &&
              <>
                {isInfluencerSignup &&
                  <Text style={styles.cancelBeforeTrialEnds}>
                    {t('cancelBeforeTrialEnds')}</Text>
                }
                {!isInfluencerSignup && !planId &&
                  <Text style={[Theme.textStyle.bodyCopyReg, {textAlign: 'center'}]}>
                    {t('noCreditCardRequired')}</Text>
                }
              </>
            }
          </View>
          {/* Enable this experiment once the new styling and layout on this screen has been normalized */}
          {/*<AbExperimentVariant*/}
          {/*  remoteConfigKey={RemoteConfigVariables.signup_screen_version}*/}
          {/*  value='A'>*/}
          {/*  <View style={styles.legalNoticeContainer}>*/}
          {/*    <Text style={styles.legalNotice}>{t('authLegalText')}</Text>*/}
          {/*    <Text style={styles.legalNotice}>{t('authLegalText2')}</Text>*/}
          {/*  </View>*/}
          {/*</AbExperimentVariant>*/}
        </ScrollView>
      </>
    );
  }
}

const styles = StyleSheet.create({
  heading: {
    ...Theme.textStyle.heading,
    color: Theme.GREY_DARK,
    ...Platform.select({
      web: {
        paddingVertical: isMobileWeb ? 15 : 0,
        color: Theme.WHITE,
      },
    }),
  },
  container: {
    backgroundColor: Theme.WHITE,
    paddingTop: 45,
    ...Platform.select({
      web: {
        backgroundColor: Theme.GREY_DARK,
      },
    }),
  },
  contentContainerStyle: {
    flexGrow: 1,
    justifyContent: 'space-between',
    flexDirection: 'column',
  },
  headingContainer: {
    marginBottom: defaultScale(46),
  },
  formContainer: {
    paddingBottom: 15,
  },
  legalNoticeContainer: {
    marginTop: 30,
    borderTopWidth: 1,
    paddingBottom: 20,
    borderTopColor: Theme.GREY_MEDIUM_LIGHT,
  },
  legalNotice: {
    fontSize: 14,
    paddingTop: 10,
    fontFamily: Theme.Custom400,
    color: Theme.GREY_MEDIUM_LIGHT,
  },
  textInput: {
    color: Theme.WHITE,
  },
  tandcText: {
    ...Theme.textStyle.bodyCopyReg,
    color: Theme.GREY_MEDIUM,
    ...Platform.select({
      web: {
        color: Theme.GREY_MEDIUM_LIGHT,
      },
    }),
  },
  noCreditCardText: {
    fontSize: 18,
    color: Theme.GREY_DARK_MEDIUM,
    textAlign: 'center',
    marginTop: 15,
    ...Platform.select({
      web: {
        color: Theme.WHITE,
        fontFamily: Theme.CustomBold,
      },
    }),
  },
  cancelBeforeTrialEnds: {
    ...Theme.textStyle.bodyCopyReg,
    textAlign: 'center',
    color: Theme.GREY_MEDIUM,
  },
});

const mapStateToProps = (state) => {
  const { workoutInviteId, utm_medium, planId } = state.deepLinkParamaters;
  return { workoutInviteId, utm_medium, planId };
};

const mapDispatchToProps = (dispatch) => ({
  clearDeepLinkParams: (params: string[]) => dispatch(clearDeepLinkParams(params)),
});

export const CompleteHomeProfile = reduxForm({ form: 'CreateHomeAccountForm' })(
  translate(['auth', 'formFields'], { wait: true })(
    UserDataConsumer(connect(mapStateToProps, mapDispatchToProps)(CompleteHomeProfileScreen))),
);
